import { MenuItem, Select } from '@mui/material';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import { IconButton } from '@design-system/components/button/iconButton';
import { Input } from '@design-system/components/input/input';
import { InputNumber } from '@design-system/components/input/inputNumber';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { bgColorsVars } from '@design-system/vars.css';
import { SwapSet, TO_FAILURE_AMOUNT } from '../model/swaps.domain';
import * as styles from './CreatorApprovedSwaps.css';

const setUnits = ['reps', 'sec', 'min', 'meters', 'calories'];

const unitSelectSx = {
  minWidth: '130px',
  height: 44,
  padding: '8px 16px',
  border: 'solid 2px',
  borderRadius: '10px',
  borderColor: bgColorsVars['N-40'],
  backgroundColor: bgColorsVars['W-100'],
  boxShadow: 'none',
  fontWeight: 500,
  fontFamily: 'inherit',
  margin: '1px',
  '&.Mui-focused': {
    boxShadow: 'none',
    borderWidth: '3px',
  },
};

const toInputValue = (
  amount: number | string | undefined,
): number | undefined => {
  if (amount === undefined || amount === null || `${amount}` === '') {
    return undefined;
  }
  const parsed = Number(amount);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const DraftSetRow = ({
  label,
  reps,
  onChange,
  onRemove,
}: {
  label: string;
  reps: SwapSet['reps'];
  onChange: (reps: SwapSet['reps']) => void;
  onRemove?: () => void;
}) => {
  const [amount, unit] = reps;
  const isToFailure = Number(amount) === TO_FAILURE_AMOUNT;

  return (
    <div className={styles.modalSetRow}>
      <Typography className={styles.modalSetLabel}>{label}</Typography>
      <div className={styles.modalSetAmount}>
        {isToFailure ? (
          <Input value="To Failure" disabled readOnly />
        ) : (
          <InputNumber
            value={toInputValue(amount)}
            onChange={(value) => onChange([value ?? '', unit])}
            min={0}
          />
        )}
      </div>
      <Select
        value={unit}
        sx={unitSelectSx}
        disabled={isToFailure}
        onChange={(event) => onChange([amount, event.target.value])}
      >
        {setUnits.map((option) => (
          <MenuItem key={option} value={option}>
            <Typography variant="body" size="large">
              {option}
            </Typography>
          </MenuItem>
        ))}
      </Select>
      <div data-testid="draft-set-to-failure">
        <Toggle
          value={isToFailure}
          variant="primary"
          onChange={(next) =>
            onChange(next ? [TO_FAILURE_AMOUNT, 'reps'] : [0, unit ?? 'reps'])
          }
        />
      </div>
      <Typography className={styles.modalToFailureLabel}>To Failure</Typography>
      <div className={styles.modalRemoveSlot}>
        {onRemove && (
          <IconButton
            variant="text"
            color="neutral"
            icon={faClose}
            onClick={onRemove}
          />
        )}
      </div>
    </div>
  );
};
